import { ReadScope, SyncType } from '../../types/sync/enums';
import { syncService } from '../../services/syncService';
import { useAcombaBridgeStore, type ExtractionEntity } from '../../stores/acombaBridgeStore';

export interface BridgeExtractionResult {
    /** Interfaces that failed or were cancelled. Non-empty ⇒ callers must abort rather than post. */
    failed: ExtractionEntity[];
    /**
     * Whether this extraction read every customer. The backend may only remove a relance the data no longer
     * justifies when it did: an incremental read says nothing about who is absent from it, so an absence there
     * means "not read", never "nothing owed".
     */
    isCompleteRead: boolean;
}

/**
 * Runs the correct Acomba bridge extraction for a sync.
 *
 * Sync **type** and read **scope** are separate axes: both Acomba modes read the whole company, and `SyncType`
 * only decides how much the backend recomputes. Reading everything is what keeps the destroyed-transaction
 * guarantee intact — see `docs/features/sync/acomba-sync.md` § BR-19. Only the missing-info retry reads less.
 *
 * Returns null when no extraction ran (one already in flight, or a company mismatch). Callers must abort on
 * null rather than post: nothing fresh was read.
 */
export async function extractBridgeData(
    syncType: SyncType,
    readScope: ReadScope = ReadScope.Complete,
): Promise<BridgeExtractionResult | null> {
    const store = useAcombaBridgeStore.getState();

    if (readScope === ReadScope.TargetedRefresh && syncType === SyncType.Partial) {
        // The incremental read needs a cutoff. With no prior sync there is nothing to be incremental against,
        // so it falls through to reading everything — strictly more than asked for, never less.
        const since = (await syncService.getStatus()).lastSyncedAt;
        if (since !== null) {
            const failed = await store.startIncrementalExtraction(since.toISOString());
            return failed === null ? null : { failed, isCompleteRead: false };
        }
    }

    const failed = await store.startExtraction();
    return failed === null ? null : { failed, isCompleteRead: true };
}
